import { FC, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import type { HotelItem, HotelSearchContextForSave } from '../../../../../types/hotel.types';
import ChooseWhereToSaveModal, {
  type ChooseWhereToSavePayload,
} from '../ChooseWhereToSaveModal/ChooseWhereToSaveModal';
import TripStopScheduleModal, {
  type TripStopScheduleInitial,
} from '../TripStopScheduleModal';
import { useDiscoverStoppableSaveFlow } from '../../hooks/useDiscoverStoppableSaveFlow';

export interface SaveHotelModalProps {
  open: boolean;
  onClose: () => void;
  hotel: HotelItem | null;
  searchContext: HotelSearchContextForSave;
}

function hotelSearchToScheduleInitial(ctx: HotelSearchContextForSave): TripStopScheduleInitial | null {
  const inDay = ctx.checkInDate?.trim().slice(0, 10);
  const outDay = ctx.checkOutDate?.trim().slice(0, 10);
  if (!inDay || !outDay) return null;
  return {
    startDate: inDay,
    startTime: '15:00',
    endDate: outDay,
    endTime: '11:00',
  };
}

const SaveHotelModal: FC<SaveHotelModalProps> = ({ open, onClose, hotel, searchContext }) => {
  const navigate = useNavigate();
  const scheduleInitial = useMemo(() => hotelSearchToScheduleInitial(searchContext), [searchContext]);

  const {
    saveError,
    setSaveError,
    chooseOpen,
    scheduleOpen,
    pendingTripSave,
    closeTripSchedule,
    openTripSchedule,
    beginTripSaveFromChoose,
    beginNewTripSave,
  } = useDiscoverStoppableSaveFlow(open);

  const handleCreateNewIdeaList = () => {
    if (!hotel) return;
    onClose();
    navigate('/ideas');
  };

  const handleCreateNewTripStub = () => {
    beginNewTripSave();
  };

  const handleTripSelect = (tripId: number) => {
    openTripSchedule({ tripId, isNewTrip: false });
  };

  const handleChooseSave = async (payload: ChooseWhereToSavePayload) => {
    setSaveError(null);
    if (!hotel) return;

    if (payload.tab === 'ideas') {
      if (payload.ideaId == null) {
        setSaveError('Select an ideas list to save this hotel.');
        return;
      }
      onClose();
      return;
    }

    if (payload.tab === 'trips') {
      beginTripSaveFromChoose(payload);
    }
  };

  const handleScheduleCreate = (_times: { tripStopStartDateTime: string; tripStopEndDateTime: string }) => {
    if (!pendingTripSave) return;
    onClose();
  };

  return (
    <>
      <ChooseWhereToSaveModal
        open={chooseOpen}
        onClose={onClose}
        titleId="save-hotel-modal-title"
        searchInputName="save-hotel-search"
        onSave={handleChooseSave}
        noIdeaListsEmptyMessage="You have no idea lists yet. Create one below."
        onCreateNewIdeaList={handleCreateNewIdeaList}
        onCreateNewTrip={handleCreateNewTripStub}
        onTripSelect={handleTripSelect}
        createNewIdeaListDisabled={!hotel}
        errorText={saveError}
        onErrorDismiss={() => setSaveError(null)}
      />

      <TripStopScheduleModal
        open={scheduleOpen}
        onClose={closeTripSchedule}
        onCreate={handleScheduleCreate}
        initialValues={scheduleInitial}
        titleId="save-hotel-stop-schedule-title"
      />
    </>
  );
};

export default SaveHotelModal;
